import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchAllResults,
  fetchKOResults,
  fetchLeaderboard,
  fetchKnockoutGames,
} from "../../api/results";

const initialState = {
  loading: false,
  koLoading: false,
  koGamesLoading: false,
  leaderboardLoading: false,
  // results_full -> objects keyed by group letter
  groupStageGames: {},
  groupStandings: {},
  groupScore: null,
  overallScore: null,
  thirdPlaceResults: [],
  // knockout (from results_full and/or results_ko)
  knockoutResults: [],
  knockoutSimple: [],
  // full knockout bracket skeleton (all matches incl. TBD) from knockout_games
  knockoutGames: [],
  // leaderboard
  leaderboard: [],
  leaderboardMeta: {
    totalEntrants: 0,
    limit: 100,
    offset: 0,
    contestId: null,
  },
  currentUser: null,
};

export const getResultsFullAction = createAsyncThunk(
  "results/getResultsFull",
  async (props, { rejectWithValue }) => {
    const { userId, contestId } = props;

    try {
      const res = await fetchAllResults(userId, contestId);

      return res;
    } catch (err) {
      console.error("Error in [getResultsFull] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getResultsKOAction = createAsyncThunk(
  "results/getResultsKO",
  async (props, { rejectWithValue }) => {
    const { userId, contestId } = props;

    try {
      const res = await fetchKOResults(userId, contestId);

      return res;
    } catch (err) {
      console.error("Error in [getResultsKO] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getKnockoutGamesAction = createAsyncThunk(
  "results/getKnockoutGames",
  async (props, { rejectWithValue }) => {
    const { contestId } = props;

    try {
      const res = await fetchKnockoutGames(contestId);

      return res;
    } catch (err) {
      console.error("Error in [getKnockoutGames] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

export const getLeaderboardAction = createAsyncThunk(
  "results/getLeaderboard",
  async (props, { rejectWithValue }) => {
    const { userId, contestId, limit, offset } = props;

    try {
      const res = await fetchLeaderboard(userId, contestId, limit, offset);

      return res;
    } catch (err) {
      console.error("Error in [getLeaderboard] action: ", err);

      return rejectWithValue(err?.response?.data);
    }
  },
);

const resultsSlice = createSlice({
  name: "results",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // ---- results_full ----
    builder.addCase(getResultsFullAction.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getResultsFullAction.fulfilled, (state, action) => {
      const payload = action.payload || {};
      state.groupStageGames = payload.group_stage_games || {};
      state.groupStandings = payload.group_standings || {};
      state.groupScore = payload.group_score || null;
      state.overallScore = payload.overall_score || null;
      state.thirdPlaceResults = payload.third_place_results || [];
      // results_full also carries knockout_results
      state.knockoutResults = payload.knockout_results || [];
      state.loading = false;
    });
    builder.addCase(getResultsFullAction.rejected, (state) => {
      state.loading = false;
    });

    // ---- results_ko ----
    builder.addCase(getResultsKOAction.pending, (state) => {
      state.koLoading = true;
    });
    builder.addCase(getResultsKOAction.fulfilled, (state, action) => {
      const payload = action.payload || {};
      state.knockoutResults = payload.knockout_results || [];
      state.knockoutSimple = payload.knockout_simple || [];
      state.koLoading = false;
    });
    builder.addCase(getResultsKOAction.rejected, (state) => {
      state.koLoading = false;
    });

    // ---- knockout_games (full bracket skeleton) ----
    builder.addCase(getKnockoutGamesAction.pending, (state) => {
      state.koGamesLoading = true;
    });
    builder.addCase(getKnockoutGamesAction.fulfilled, (state, action) => {
      const payload = action.payload || {};
      // controller returns games grouped by round_name; flatten to a list
      const grouped = payload.knockout_games || {};
      state.knockoutGames = Object.values(grouped).flat();
      state.koGamesLoading = false;
    });
    builder.addCase(getKnockoutGamesAction.rejected, (state) => {
      state.koGamesLoading = false;
    });

    // ---- leaderboard ----
    builder.addCase(getLeaderboardAction.pending, (state) => {
      state.leaderboardLoading = true;
    });
    builder.addCase(getLeaderboardAction.fulfilled, (state, action) => {
      const payload = action.payload || {};
      state.leaderboard = payload.leaderboard || [];
      state.leaderboardMeta = {
        totalEntrants: payload.total_entrants || 0,
        limit: payload.limit ?? 100,
        offset: payload.offset ?? 0,
        contestId: payload.contest_id ?? null,
      };
      state.currentUser = payload.current_user || null;
      state.leaderboardLoading = false;
    });
    builder.addCase(getLeaderboardAction.rejected, (state) => {
      state.leaderboardLoading = false;
    });
  },
});

export default resultsSlice.reducer;
